<script setup lang="ts">
import Carousel from "primevue/carousel";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { Excursion } from "@/components/RegistrationProcess/types/events.ts";
import { BREAKPOINTS, useMobile } from "@/composables/useMobile";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import ExcursionIncludedItem from "../ExcursionIncludedItem/index.vue";
import { carouselScrollbarPreset } from "./styles.ts";
import type { Props } from "./types.ts";

defineOptions({
  name: "ExcursionIncludedSection",
});

const DISPLAY_COUNT: number = 3;
const MOBILE_DISPLAY_COUNT: number = 1;
const INCLUDED_ITEM_WIDTH_PX: number = 393;

const modelValue = defineModel<Excursion[]>({ required: true });
const props = withDefaults(defineProps<Props>(), {
  title: "Included Experiences",
});
// `secondaryColor` is the app-wide "branded storefront" signal (see Menu.vue, the experience
// footer, the legacy excursion cards). Branded tenants get a gray accent band with navy text —
// the storefront color is reserved for controls, not for surfaces and copy.
const { secondaryColor, primaryColorClassBG } = useTenantTheming();
// Viewport width, not `isMobile`: the card's own layout switches on `md:` classes, so the
// carousel has to agree with those rather than with device/pointer detection.
const { isNarrowViewport } = useMobile();

// Carousel prev/next buttons follow the storefront primary branding.
const carouselPreset = computed(() => carouselScrollbarPreset(primaryColorClassBG.value));

// Show one card at a time on mobile so the fixed-width desktop carousel never overflows the
// viewport. PrimeVue compiles this to `@media (max-width: <breakpoint>)`, which is inclusive,
// so one pixel below `md` flips it exactly where the cards' `md:` styles flip.
const responsiveOptions = [{ breakpoint: `${BREAKPOINTS.md - 1}px`, numVisible: MOBILE_DISPLAY_COUNT, numScroll: 1 }];

const displayCount = computed(() =>
  modelValue.value.length < DISPLAY_COUNT ? modelValue.value.length : DISPLAY_COUNT,
);

const visibleCount = computed(() =>
  isNarrowViewport.value ? Math.min(modelValue.value.length, MOBILE_DISPLAY_COUNT) : displayCount.value,
);

const showNavigation = computed(() => modelValue.value.length > visibleCount.value);

// On mobile a card fills the width, so prev/next would land on top of the card's own content
// (they are absolutely positioned at the carousel's mid-height). Paging there is the dots plus
// the Carousel's built-in touch swipe; the arrows are desktop-only, where they sit outside the
// cards.
const showNavigatorArrows = computed(() => showNavigation.value && !isNarrowViewport.value);

// The fixed-width compact sizing is desktop-only; on mobile the carousel fills the card width.
const useCompactCarousel = computed(
  () => !isNarrowViewport.value && modelValue.value.length > 0 && modelValue.value.length < DISPLAY_COUNT,
);
const compactCarouselWidth = computed(() => `${displayCount.value * INCLUDED_ITEM_WIDTH_PX}px`);

// The arrows sit on the seam between a card's image and its text, not at the carousel's mid-height.
// PrimeVue has no option for that: its nav buttons are absolutely positioned against the carousel
// container and know nothing about the item template, so `pt` can style them but cannot tell them
// where the seam is. Hence measuring the first card's image and publishing the offset as a custom
// property the preset reads. On desktop the seam happens to land at a steady 265px — the card caps
// at `md:max-w-[377px]` and the image floors at a 212px min-height — but deriving it rather than
// hardcoding that keeps the arrows on the seam if the band copy, card padding or image sizing
// changes. Falls back to the carousel's mid-height until the first measurement lands.
const carouselRoot = ref<HTMLElement>();
const navTopOffset = ref<string>("50%");

const measureNavOffset = (): void => {
  const root = carouselRoot.value;
  const media = root?.querySelector<HTMLElement>("[data-excursion-card-media]");
  if (!root || !media) return;

  const rootRect = root.getBoundingClientRect();
  const mediaRect = media.getBoundingClientRect();
  // The configurator hides this section behind `display: none` until its images have loaded, and
  // a hidden element measures as all zeros. Skipping that keeps the fallback in place; the
  // ResizeObserver re-measures as soon as the section gets a box.
  if (rootRect.height === 0 || mediaRect.height === 0) return;

  navTopOffset.value = `${Math.round(mediaRect.bottom - rootRect.top)}px`;
};

let navResizeObserver: ResizeObserver | undefined;

onMounted(async () => {
  await nextTick();
  measureNavOffset();

  if (typeof ResizeObserver === "undefined" || !carouselRoot.value) return;
  navResizeObserver = new ResizeObserver(measureNavOffset);
  navResizeObserver.observe(carouselRoot.value);
});

onBeforeUnmount(() => {
  navResizeObserver?.disconnect();
  navResizeObserver = undefined;
});

// Cards arriving (or the count crossing the single-card breakpoint) changes the seam.
watch(
  () => modelValue.value.length,
  async () => {
    await nextTick();
    measureNavOffset();
  },
);

const carouselStyle = computed<Record<string, string>>(() => ({
  ...(useCompactCarousel.value ? { width: compactCarouselWidth.value } : {}),
  "--carousel-nav-top": navTopOffset.value,
}));
</script>

<template>
  <div class="shadow-[0px_4px_20px_0px_#00000026]" data-testid="excursion-included-section">
    <div
      class="flex w-full py-2 px-4 justify-start items-center"
      :class="secondaryColor ? 'bg-gray-275' : 'bg-blue-400'"
    >
      <span class="text-xl font-bold text-university-primary" data-testid="excursion-included-section-title">
        {{ title }}
      </span>
    </div>
    <div class="flex flex-col gap-4 p-4">
      <div class="w-full text-university-primary">
        <span data-testid="excursion-included-section-description">A curated set of immersive excursions to help you explore and connect with your destination, included at no extra cost.</span>
      </div>
      <div
        ref="carouselRoot"
        :class="useCompactCarousel ? 'max-w-full' : 'w-full'"
        :style="carouselStyle"
        data-testid="excursion-included-carousel"
      >
        <Carousel
          :value="modelValue"
          :numVisible="displayCount"
          :responsiveOptions="responsiveOptions"
          :showIndicators="showNavigation"
          :showNavigators="showNavigatorArrows"
          :numScroll="1"
          :circular="showNavigation"
          :pt="carouselPreset"
        >
          <template #item="{ data: excursion }">
            <ExcursionIncludedItem :model-value="excursion" />
          </template>
        </Carousel>
      </div>
    </div>
  </div>
</template>
